import { ReactNode } from 'react';

import {
  CalendarOutlined,
  CarOutlined,
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  InboxOutlined,
  PercentageOutlined,
  ShopOutlined,
} from '@ant-design/icons';
import { Skeleton } from 'antd';
import { useTranslation } from 'react-i18next';

import { TiendaPerfectaVisitHistoryKpis } from 'models/TiendaPerfecta';

import { complianceLevel, coveragePct } from '../utils/compliance';
import { formatDecimal, formatInt } from '../utils/format';

const I18N_BASE = 'scenes.visit_history.kpis';

interface KpiCardProps {
  icon: ReactNode;
  label: string;
  value: ReactNode;
  hint: string;
  /** Compliance only: a fixed semantic color, never from `colorPrimary`. */
  level?: 'ok' | 'warn' | 'bad';
  /** Executed only: its number is Navy, like its bars. */
  executed?: boolean;
}

function KpiCard({ icon, label, value, hint, level, executed }: KpiCardProps) {
  return (
    <div className="visit-history-kpi">
      <div className="visit-history-kpi__top">
        {icon}
        {label}
      </div>
      <div
        className={`visit-history-kpi__value${
          level ? ` visit-history-kpi__value--${level}` : ''
        }${executed ? ' visit-history-kpi__value--executed' : ''}`}
      >
        {value}
      </div>
      <div className="visit-history-kpi__hint">{hint}</div>
    </div>
  );
}

interface VisitHistoryKpisProps {
  kpis: TiendaPerfectaVisitHistoryKpis | null;
  loading?: boolean;
}

/** The seven period counters. Without data a zero is never invented. */
function VisitHistoryKpis({ kpis, loading = false }: VisitHistoryKpisProps) {
  const { t, i18n } = useTranslation();
  const language = i18n.language ?? 'es';
  const ready = !loading && kpis !== null;

  const value = (text: string | undefined) =>
    ready && text !== undefined ? (
      text
    ) : (
      <Skeleton.Input active style={{ width: 58, height: 38 }} />
    );
  const int = (n: number | undefined) =>
    value(n === undefined ? undefined : formatInt(n, language));

  const coverage = kpis
    ? coveragePct(kpis.executed, kpis.incidents, kpis.planned)
    : undefined;

  return (
    <div className="visit-history-kpis">
      <KpiCard
        icon={<CalendarOutlined />}
        label={t(`${I18N_BASE}.planned.label`)}
        value={int(kpis?.planned)}
        hint={t(`${I18N_BASE}.planned.hint`)}
      />
      <KpiCard
        icon={
          <CheckCircleOutlined className="visit-history-kpi__icon--executed" />
        }
        label={t(`${I18N_BASE}.executed.label`)}
        value={int(kpis?.executed)}
        hint={t(`${I18N_BASE}.executed.hint`)}
        executed
      />
      <KpiCard
        icon={
          <ExclamationCircleOutlined className="visit-history-kpi__icon--incident" />
        }
        label={t(`${I18N_BASE}.incidents.label`)}
        value={int(kpis?.incidents)}
        hint={t(`${I18N_BASE}.incidents.hint`, {
          count: ready ? kpis?.pending ?? 0 : 0,
        })}
      />
      <KpiCard
        icon={<ShopOutlined />}
        label={t(`${I18N_BASE}.coverage.label`)}
        value={value(
          coverage === undefined
            ? undefined
            : `${formatDecimal(coverage, language)}%`
        )}
        hint={t(`${I18N_BASE}.coverage.hint`)}
        level={
          ready && coverage !== undefined
            ? complianceLevel(coverage)
            : undefined
        }
      />
      <KpiCard
        icon={<PercentageOutlined />}
        label={t(`${I18N_BASE}.compliance.label`)}
        value={value(
          kpis ? `${formatDecimal(kpis.compliancePct, language)}%` : undefined
        )}
        hint={t(`${I18N_BASE}.compliance.hint`)}
        level={ready && kpis ? complianceLevel(kpis.compliancePct) : undefined}
      />
      <KpiCard
        icon={<CarOutlined />}
        label={t(`${I18N_BASE}.routes.label`)}
        value={int(kpis?.routes)}
        hint={t(`${I18N_BASE}.routes.hint`)}
      />
      <KpiCard
        icon={<InboxOutlined />}
        label={t(`${I18N_BASE}.material.label`)}
        value={int(kpis?.materialUnits)}
        hint={t(`${I18N_BASE}.material.hint`)}
      />
    </div>
  );
}

export default VisitHistoryKpis;
